import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useEntitlements } from '../context/EntitlementsContext';
import { useUpgrade } from '../context/UpgradeContext';
import { useProjectsAPI } from '../hooks/useProjectsAPI';
import AppHeader from '../components/AppHeader';
import BottomNav from '../components/BottomNav';
import { Plus, DoorOpen, Camera, Warning, CheckCircle, MapPin, PencilSimple, CircleNotch, Lock } from '@phosphor-icons/react';
import { compressImage } from '../lib/imageCompress';
import { toast } from 'sonner';
import AiAnalysisCard from '../components/AiAnalysisCard';
import EmptyState from '../components/EmptyState';
import BuilderResponseHomeownerView from '../components/BuilderResponseHomeownerView';
import PropertyBuildersCard from '../components/PropertyBuildersCard';
import PhotoAnnotator, { AnnotationOverlay } from '../components/PhotoAnnotator';
import WalkAroundCapture from '../components/WalkAroundCapture';


export default function ProjectDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const ent = useEntitlements();
    const upgrade = useUpgrade();
    const store = useProjectsAPI(!!user?.user_id);
    const navigate = useNavigate();
    const [filter, setFilter] = useState('all');
    const [showNewRoom, setShowNewRoom] = useState(false);
    const [roomName, setRoomName] = useState('');
    const [showAddSnag, setShowAddSnag] = useState(false);
    const [editingSnagId, setEditingSnagId] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    // When we land here from a `?snag=<id>` deep-link (e.g. from the
    // Floorplan pins list "Edit" button), auto-open that snag's edit
    // pane once the store has hydrated and scroll to it. We guard with
    // a ref so the effect fires exactly once per navigation.
    const consumedSnagParam = useRef(null);
    // `editingMeta` MUST stay declared above the `if (!store.ready)` and
    // `if (!project)` early returns. Moving it below caused a Rules-of-
    // Hooks violation (the hook count on the first render — when the
    // store hadn't finished hydrating — was lower than on subsequent
    // renders), which crashed React with "Rendered more hooks than
    // during the previous render" and produced a blank screen on every
    // property open.
    const [editingMeta, setEditingMeta] = useState(false);
    const [showWalkAround, setShowWalkAround] = useState(false);

    const project = useMemo(() => store.state.projects.find(p => p.id === id), [store.state.projects, id]);

    // Deep-link handler: `?snag=<id>` opens that snag's edit view and
    // scrolls to it. Runs once per navigation — we track the consumed
    // id in a ref so re-renders don't re-fire it after the user has
    // closed / edited the target snag.
    useEffect(() => {
        if (!project) return;
        const targetId = searchParams.get('snag');
        if (!targetId || consumedSnagParam.current === targetId) return;
        const snag = project.snags?.find(s => s.id === targetId);
        if (!snag) return;
        consumedSnagParam.current = targetId;
        setEditingSnagId(targetId);
        // Strip the query so a page reload doesn't re-trigger the edit.
        setSearchParams({}, { replace: true });
        // Scroll after the edit row renders. requestAnimationFrame keeps
        // us aligned with the paint that inserts <SnagForm>.
        requestAnimationFrame(() => {
            const row = document.querySelector(`[data-testid="snag-edit-row-${targetId}"]`)
                     || document.querySelector(`[data-testid="snag-row-${targetId}"]`);
            if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    }, [project, searchParams, setSearchParams]);

    if (!store.ready) return null;
    if (!project) return (
        <div className="min-h-screen flex items-center justify-center">
            <button onClick={() => navigate('/app')} className="underline">Back to properties</button>
        </div>
    );

    const filtered = project.snags.filter(s => filter === 'all' ? true : s.status === filter);

    const handleSaveMeta = async ({ name, address, developer_name }) => {
        const patch = {};
        // Only send fields that actually changed — keeps the patch small
        // and avoids stamping defaults onto fields we don't own.
        if (name !== project.name) patch.name = name;
        if ((address || '') !== (project.address || '')) patch.address = address || '';
        if ((developer_name || '') !== (project.developer_name || '')) patch.developer_name = developer_name || '';
        if (Object.keys(patch).length === 0) {
            setEditingMeta(false);
            return;
        }
        await store.updateProject(project.id, patch);
        toast.success('Property details updated');
        setEditingMeta(false);
    };

    const roomName_ = (roomId) => project.rooms.find(r => r.id === roomId)?.name || '—';

    const out = project.snags.filter(s => s.status === 'outstanding').length;
    const done = project.snags.filter(s => s.status === 'completed').length;

    return (
        <div className="min-h-screen bg-white pb-24">
            <AppHeader title={project.name} subtitle={[project.address, project.developer_name].filter(Boolean).join(' · ') || 'New build property'} back />

            <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
                {/* Edit details affordance — sits subtly above the stats so
                    a homeowner who skipped the developer field on creation
                    has a clear path back. */}
                <div className="flex items-center justify-end mb-3 -mt-2">
                    <button
                        onClick={() => setEditingMeta(true)}
                        data-testid="edit-property-meta-btn"
                        className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.18em] text-[#52525B] hover:text-[#1787AB] underline decoration-[#1FA5CF] underline-offset-4"
                    >
                        <PencilSimple size={12} weight="bold" /> Edit details
                    </button>
                </div>
                {/* Stats */}
                <div className="grid grid-cols-2 gap-0 border border-[#18181B] mb-6">
                    <div className="p-4 border-r border-[#18181B]">
                        <div className="label-tiny">Outstanding</div>
                        <div className="font-display text-3xl font-bold text-[#E53935] mt-1" data-testid="stat-outstanding">{out}</div>
                    </div>
                    <div className="p-4">
                        <div className="label-tiny">Completed</div>
                        <div className="font-display text-3xl font-bold text-[#00875A] mt-1" data-testid="stat-completed">{done}</div>
                    </div>
                </div>

                <PropertyBuildersCard propertyId={project.id} />

                {/* Rooms */}
                <div className="flex items-center justify-between mb-3">
                    <div className="label-tiny">Rooms ({project.rooms.length})</div>
                    <button
                        onClick={() => setShowNewRoom(v => !v)}
                        data-testid="add-room-btn"
                        className="text-xs font-mono uppercase tracking-[0.18em] underline decoration-[#1FA5CF] underline-offset-4"
                    >
                        + Add room
                    </button>
                </div>

                {showNewRoom && (
                    <form
                        onSubmit={(e) => { e.preventDefault(); if (!roomName.trim()) return; store.addRoom(project.id, roomName.trim()); setRoomName(''); setShowNewRoom(false); }}
                        className="grid-border p-3 mb-4 flex gap-2 fade-up"
                        data-testid="new-room-form"
                    >
                        <input
                            data-testid="room-name-input"
                            autoFocus
                            value={roomName}
                            onChange={(e) => setRoomName(e.target.value)}
                            placeholder="Room name"
                            className="flex-1 border border-[#E4E4E7] focus:border-[#18181B] outline-none px-3 py-2"
                        />
                        <button className="bg-[#09090B] text-white px-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#27272A] active:scale-95 transition-all duration-150">Add</button>
                    </form>
                )}

                <div className="flex gap-2 overflow-x-auto pb-3 mb-4 -mx-1 px-1">
                    {project.rooms.map(r => {
                        const snagCount = project.snags.filter(s => s.roomId === r.id).length;
                        return (
                            <div
                                key={r.id}
                                data-testid={`room-chip-${r.id}`}
                                className="flex-none min-w-[140px] border border-[#18181B] p-3 hover:bg-[#F4F4F5] transition-all duration-150"
                            >
                                <DoorOpen size={18} weight="duotone" color="#1FA5CF" />
                                <div className="font-display font-semibold mt-1 truncate">{r.name}</div>
                                <div className="text-xs text-[#52525B] font-mono">{snagCount} defect{snagCount !== 1 ? 's' : ''}</div>
                            </div>
                        );
                    })}
                </div>

                {/* Filter */}
                <div className="flex items-center justify-between mb-3">
                    <div className="label-tiny">Defects</div>
                    <div className="flex items-center gap-0 border border-[#18181B]">
                        {['all', 'outstanding', 'completed'].map(f => (
                            <button
                                key={f}
                                onClick={() => setFilter(f)}
                                data-testid={`filter-${f}`}
                                className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] ${filter === f ? 'bg-[#09090B] text-white' : 'text-[#52525B]'}`}
                            >{f}</button>
                        ))}
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 mb-4">
                    <button
                        onClick={() => setShowAddSnag(true)}
                        data-testid="add-snag-btn"
                        className="flex-1 bg-[#1FA5CF] text-white py-4 font-mono text-sm uppercase tracking-[0.2em] hover:bg-[#1787AB] active:scale-95 transition-all duration-150 inline-flex items-center justify-center gap-2"
                    >
                        <Plus size={18} weight="bold" /> Log new defect
                    </button>
                    {/* Walk-around quick-capture — sped-up flow that turns
                        each photo into a queued defect with one tap. Best
                        for the first walk-through of a property when there
                        might be 20+ things to log. Photos are batched and
                        saved together when the user is done. */}
                    <button
                        onClick={() => setShowWalkAround(true)}
                        data-testid="walkaround-open-btn"
                        className="bg-[#09090B] text-white py-4 px-5 font-mono text-sm uppercase tracking-[0.2em] hover:bg-[#27272A] active:scale-95 transition-all duration-150 inline-flex items-center justify-center gap-2"
                    >
                        <Camera size={18} weight="bold" /> Walk-around
                    </button>
                </div>

                {showAddSnag && (
                    <SnagForm
                        rooms={project.rooms}
                        onCancel={() => setShowAddSnag(false)}
                        onSubmit={async (data) => {
                            // Await the snag write before unmounting the form
                            // so React doesn't render the EmptyState in the
                            // brief window between "form hidden" and "store
                            // state refreshed". Returning the result lets
                            // SnagForm detect a 402 paywall and skip its own
                            // success toast.
                            const result = await store.addSnag(project.id, data);
                            if (result !== undefined) setShowAddSnag(false);
                            return result;
                        }}
                    />
                )}

                {filtered.length === 0 ? (
                    filter === 'all' && project.snags.length === 0 && !showAddSnag ? (
                        <EmptyState
                            icon={Camera}
                            eyebrow="Defect log"
                            title="Capture your first defect"
                            body="Spot a hairline crack, a wonky skirting, a missing seal? Tap below to log it with a photo, room, and notes. Add a floorplan from the Plan tab and you can pin defects exactly where they are."
                            cta={{
                                label: 'Log a defect',
                                icon: Plus,
                                onClick: () => setShowAddSnag(true),
                                testId: 'empty-add-snag-btn',
                            }}
                            secondary={{
                                label: 'Open plan view',
                                icon: MapPin,
                                onClick: () => navigate(`/app/floorplan?id=${project.id}`),
                                testId: 'empty-open-plan-btn',
                            }}
                            testId="empty-snags"
                        />
                    ) : (
                        <div className="grid-border p-8 text-center text-sm text-[#52525B]">
                            No defects {filter !== 'all' ? `(${filter})` : 'logged yet'}.
                        </div>
                    )
                ) : (
                    <ul className="divide-y divide-[#E4E4E7] border border-[#E4E4E7]">
                        {filtered.map((s, i) => editingSnagId === s.id ? (
                            <li key={s.id} className="p-0" data-testid={`snag-edit-row-${s.id}`}>
                                {/* Breadcrumb — helps users orient themselves,
                                    especially when they land here via a
                                    deep-link (?snag=…) or the Floorplan
                                    'Edit' button, without having navigated
                                    through Home → Property → Defect. */}
                                <nav
                                    aria-label="Breadcrumb"
                                    data-testid={`snag-breadcrumb-${s.id}`}
                                    className="px-4 py-2 border-b border-[#E4E4E7] bg-[#FAFAFA] font-mono text-[10px] uppercase tracking-[0.18em] text-[#71717A] flex items-center gap-1.5 flex-wrap"
                                >
                                    <button
                                        type="button"
                                        onClick={() => navigate('/app')}
                                        data-testid={`snag-breadcrumb-home-${s.id}`}
                                        className="hover:text-[#1FA5CF] hover:underline"
                                    >Home</button>
                                    <span aria-hidden="true">›</span>
                                    <button
                                        type="button"
                                        onClick={() => setEditingSnagId(null)}
                                        data-testid={`snag-breadcrumb-property-${s.id}`}
                                        className="hover:text-[#1FA5CF] hover:underline truncate max-w-[45%]"
                                        title={project.name}
                                    >{project.name}</button>
                                    <span aria-hidden="true">›</span>
                                    <span className="text-[#09090B] truncate flex-1 min-w-0" title={s.title}>
                                        {s.pinNumber ? <span className="text-[#1FA5CF] mr-1">#{s.pinNumber}</span> : null}
                                        {s.title}
                                    </span>
                                </nav>
                                <SnagForm
                                    rooms={project.rooms}
                                    isEdit
                                    initial={{ title: s.title, roomId: s.roomId, notes: s.notes, severity: s.severity, photo: s.photo, photo_annotations: s.photo_annotations }}
                                    onCancel={() => setEditingSnagId(null)}
                                    onSubmit={(data) => { store.updateSnag(project.id, s.id, data); setEditingSnagId(null); }}
                                />
                            </li>
                        ) : (
                            <li
                                key={s.id}
                                className="p-4 flex items-start gap-3 hover:bg-[#F4F4F5] transition-all duration-150 fade-up"
                                style={{ animationDelay: `${i * 40}ms` }}
                                data-testid={`snag-row-${s.id}`}
                            >
                                <div className="w-16 h-16 bg-[#F4F4F5] border border-[#E4E4E7] flex-none overflow-hidden relative">
                                    {s.photo ? (
                                        <>
                                            <img src={s.photo} alt="" className="w-full h-full object-cover" />
                                            {s.photo_annotations?.length > 0 && (
                                                // Tiny corner badge so the user can tell
                                                // at a glance which thumbnails carry
                                                // annotations without having to render
                                                // the full vector overlay at 64×64.
                                                <span
                                                    className="absolute bottom-0.5 right-0.5 bg-[#1FA5CF] text-white font-mono text-[9px] px-1 leading-tight"
                                                    title={`${s.photo_annotations.length} annotation(s)`}
                                                    data-testid={`snag-thumb-annotations-${s.id}`}
                                                >
                                                    <PencilSimple size={9} weight="bold" />
                                                </span>
                                            )}
                                        </>
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-[#A1A1AA]"><Camera size={22} /></div>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-display font-semibold truncate">
                                        {s.pinNumber && (
                                            <span className="inline-flex items-center justify-center w-5 h-5 bg-[#1FA5CF] text-white font-mono text-[10px] font-bold mr-2 align-middle">{s.pinNumber}</span>
                                        )}
                                        {s.title}
                                    </div>
                                    <div className="text-xs text-[#52525B] truncate">{roomName_(s.roomId)}{s.notes ? ` · ${s.notes}` : ''}</div>
                                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                                        <button
                                            onClick={() => store.updateSnag(project.id, s.id, { status: s.status === 'completed' ? 'outstanding' : 'completed' })}
                                            data-testid={`toggle-snag-${s.id}`}
                                            className={`font-mono text-[10px] uppercase tracking-wider px-2 py-1 ${s.status === 'completed' ? 'bg-[#00875A] text-white' : 'bg-[#E53935] text-white'}`}
                                        >
                                            {s.status === 'completed' ? <span className="inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" /> Completed</span> : <span className="inline-flex items-center gap-1"><Warning size={12} weight="fill" /> Outstanding</span>}
                                        </button>
                                        <button
                                            onClick={() => setEditingSnagId(s.id)}
                                            data-testid={`edit-snag-${s.id}`}
                                            className="font-mono text-[10px] uppercase tracking-wider px-2 py-1 border border-[#18181B] text-[#09090B] hover:bg-[#F4F4F5] inline-flex items-center gap-1"
                                        >
                                            <PencilSimple size={12} weight="bold" /> Edit
                                        </button>
                                        <button
                                            onClick={() => navigate('/app/floorplan', { state: { editPinForSnagId: s.id, projectId: project.id } })}
                                            data-testid={`edit-pin-${s.id}`}
                                            className="font-mono text-[10px] uppercase tracking-wider px-2 py-1 border border-[#18181B] text-[#09090B] hover:bg-[#F4F4F5] inline-flex items-center gap-1"
                                        >
                                            <MapPin size={12} weight="bold" /> {s.pin ? 'Move pin' : 'Set pin'}
                                        </button>
                                        {s.pin && (
                                            <button
                                                onClick={() => navigate('/app/floorplan', { state: { showPinForSnagId: s.id, projectId: project.id } })}
                                                data-testid={`show-pin-${s.id}`}
                                                title="Locate this pin on the floorplan"
                                                className="font-mono text-[10px] uppercase tracking-wider px-2 py-1 border border-[#1FA5CF] text-[#1FA5CF] hover:bg-[#E8F5FB] inline-flex items-center gap-1"
                                            >
                                                <MapPin size={12} weight="fill" /> Show pin
                                            </button>
                                        )}
                                        <button
                                            onClick={() => {
                                                // Native confirm() is silently blocked in iOS PWA
                                                // standalone mode — use a sonner toast confirm
                                                // that renders reliably everywhere.
                                                const t = toast(`Delete "${s.title}"?`, {
                                                    description: 'This cannot be undone.',
                                                    duration: 8000,
                                                    action: {
                                                        label: 'Delete',
                                                        onClick: () => store.deleteSnag(project.id, s.id),
                                                    },
                                                    cancel: { label: 'Cancel', onClick: () => toast.dismiss(t) },
                                                });
                                            }}
                                            data-testid={`delete-snag-${s.id}`}
                                            className="font-mono text-[10px] uppercase tracking-wider px-2 py-1 border border-[#E53935] text-[#E53935] hover:bg-[#FDECEA] inline-flex items-center gap-1"
                                        >Delete</button>
                                    </div>
                                    <AiAnalysisCard
                                        snag={s}
                                        onAnalyse={() => store.analyseSnag(project.id, s.id)}
                                        onUpgrade={(code) => upgrade?.show?.({ property: project, reason: code })}
                                    />
                                    <BuilderResponseHomeownerView
                                        snag={s}
                                        onDispute={(patch) => store.updateSnag(project.id, s.id, patch)}
                                    />
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </main>
            <BottomNav />
            {editingMeta && (
                <PropertyMetaModal
                    project={project}
                    onCancel={() => setEditingMeta(false)}
                    onSave={handleSaveMeta}
                />
            )}
            {showWalkAround && (
                <WalkAroundCapture
                    propertyId={project.id}
                    rooms={project.rooms}
                    onClose={() => setShowWalkAround(false)}
                    onSubmit={async (defects) => {
                        // Save each defect sequentially through the
                        // existing addSnag path. This preserves the
                        // optimistic UI, offline-queue and 402 paywall
                        // handling that ProjectDetail's regular Log
                        // flow uses. We deliberately don't parallelise
                        // — homeowners are usually on patchy site WiFi
                        // and rapid-fire parallel posts get them rate
                        // limited.
                        for (const d of defects) {
                            await store.addSnag(project.id, d);
                        }
                    }}
                />
            )}
        </div>
    );
}


function PropertyMetaModal({ project, onCancel, onSave }) {
    const [name, setName] = useState(project.name || '');
    const [address, setAddress] = useState(project.address || '');
    const [developer, setDeveloper] = useState(project.developer_name || '');
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim() || saving) return;
        setSaving(true);
        try {
            await onSave({
                name: name.trim(),
                address: address.trim(),
                developer_name: developer.trim(),
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
            data-testid="edit-property-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => { if (e.target === e.currentTarget && !saving) onCancel(); }}
        >
            <form
                onSubmit={submit}
                className="bg-white w-full max-w-md border-2 border-[#18181B]"
            >
                <div className="border-b border-[#18181B] p-4 flex items-center gap-2">
                    <PencilSimple size={20} weight="duotone" color="#1FA5CF" />
                    <span className="font-display font-semibold flex-1">Edit property details</span>
                </div>
                <div className="p-5 space-y-3">
                    <div>
                        <label htmlFor="meta-name" className="label-tiny block mb-1.5">Name</label>
                        <input
                            id="meta-name"
                            data-testid="meta-name-input"
                            value={name}
                            onChange={(e) => setName(e.target.value.slice(0, 120))}
                            className="w-full border border-[#E4E4E7] focus:border-[#18181B] outline-none px-3 py-2.5"
                            disabled={saving}
                            required
                        />
                    </div>
                    <div>
                        <label htmlFor="meta-address" className="label-tiny block mb-1.5">Address</label>
                        <input
                            id="meta-address"
                            data-testid="meta-address-input"
                            value={address}
                            onChange={(e) => setAddress(e.target.value.slice(0, 200))}
                            placeholder="Optional"
                            className="w-full border border-[#E4E4E7] focus:border-[#18181B] outline-none px-3 py-2.5"
                            disabled={saving}
                        />
                    </div>
                    <div>
                        <label htmlFor="meta-developer" className="label-tiny block mb-1.5">Developer / Builder</label>
                        <input
                            id="meta-developer"
                            data-testid="meta-developer-input"
                            value={developer}
                            onChange={(e) => setDeveloper(e.target.value.slice(0, 120))}
                            placeholder="e.g. Persimmon Homes"
                            className="w-full border border-[#E4E4E7] focus:border-[#18181B] outline-none px-3 py-2.5"
                            disabled={saving}
                        />
                        <p className="mt-1 text-[10px] text-[#52525B]">
                            Helps us match your property to the right warranty templates and surfaces on the NHBC claim form.
                        </p>
                    </div>
                </div>
                <div className="border-t border-[#E4E4E7] p-4 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={saving}
                        data-testid="meta-cancel-btn"
                        className="px-3 py-2 border border-[#18181B] text-[#09090B] hover:bg-[#F4F4F5] font-mono text-[10px] uppercase tracking-[0.18em] disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={saving || !name.trim()}
                        data-testid="meta-save-btn"
                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#1FA5CF] text-white hover:bg-[#1787AB] font-mono text-[10px] uppercase tracking-[0.18em] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {saving && <CircleNotch size={12} weight="bold" className="animate-spin" />}
                        Save
                    </button>
                </div>
            </form>
        </div>
    );
}


function SnagForm({ rooms, initial, onCancel, onSubmit, isEdit = false }) {
    const [title, setTitle] = useState(initial?.title || '');
    const [roomId, setRoomId] = useState(initial?.roomId ?? (rooms[0]?.id || ''));
    const [notes, setNotes] = useState(initial?.notes || '');
    const [severity, setSeverity] = useState(initial?.severity || 'minor');
    const [photo, setPhoto] = useState(initial?.photo || null);
    // Vector annotations layered over the photo. Stored as 0..1
    // normalised coordinates so they re-render crisply at any size —
    // particularly important for the PDF export. Mirrors the same
    // shape used by Floorplan.jsx pin photos.
    const [photoAnnotations, setPhotoAnnotations] = useState(initial?.photo_annotations || []);
    const [annotatorOpen, setAnnotatorOpen] = useState(false);
    const [photoProcessing, setPhotoProcessing] = useState(false);
    const [saving, setSaving] = useState(false);

    const onFile = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f) return;
        setPhotoProcessing(true);
        try {
            const raw = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(f);
            });
            const compressed = await compressImage(raw, 1400, 0.7);
            setPhoto(compressed);
        } catch (err) {
            console.error('Photo processing failed', err);
            toast.error('Could not read that photo. Try another.');
        } finally {
            setPhotoProcessing(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!title.trim() || saving || photoProcessing) return;
        setSaving(true);
        try {
            const result = await onSubmit({ title: title.trim(), roomId: roomId || null, notes: notes.trim(), severity, photo, photo_annotations: photoAnnotations });
            // `onSubmit` (i.e. store.addSnag / updateSnag) returns undefined
            // when it hit a 402 paywall — in that case the hook already
            // surfaced the upgrade modal so we silently bail rather than
            // claiming success.
            if (result === undefined && !isEdit) { setSaving(false); return; }
            toast.success(isEdit ? 'Defect updated' : 'Defect logged');
        } catch (err) {
            console.error('Save failed', err);
            const msg = err?.name === 'QuotaExceededError' || /quota/i.test(err?.message || '')
                ? 'Your browser is full. Remove some older photos before adding new ones.'
                : 'Could not save. Please try again.';
            toast.error(msg);
            setSaving(false);
        }
    };

    return (
        <form
            onSubmit={handleSubmit}
            className="grid-border p-4 mb-4 fade-up bg-white"
            data-testid={isEdit ? 'snag-edit-form' : 'snag-form'}
        >
            <div className="label-tiny mb-3">{isEdit ? 'Edit defect' : 'New defect'}</div>
            <input
                data-testid="snag-title-input"
                autoFocus
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Scratch on kitchen worktop"
                className="w-full border border-[#E4E4E7] focus:border-[#18181B] outline-none px-3 py-3 font-display"
            />
            <select
                data-testid="snag-room-select"
                value={roomId || ''}
                onChange={(e) => setRoomId(e.target.value || null)}
                className="w-full mt-3 border border-[#E4E4E7] focus:border-[#18181B] outline-none px-3 py-3 bg-white"
            >
                <option value="">— Select a room —</option>
                {rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <div className="mt-3">
                <div className="label-tiny mb-2">Severity</div>
                <div className="grid grid-cols-4 gap-2" data-testid="snag-severity-picker">
                    {[
                        { v: 'cosmetic', label: 'Cosmetic', color: '#A1A1AA' },
                        { v: 'minor',    label: 'Minor',    color: '#1FA5CF' },
                        { v: 'major',    label: 'Major',    color: '#F59E0B' },
                        { v: 'urgent',   label: 'Urgent',   color: '#991B1B' },
                    ].map(opt => (
                        <button
                            key={opt.v}
                            type="button"
                            onClick={() => setSeverity(opt.v)}
                            data-testid={`snag-severity-${opt.v}`}
                            className={`font-mono text-[10px] uppercase tracking-[0.12em] py-2 border transition-all duration-150 ${severity === opt.v ? 'text-white' : 'bg-white text-[#09090B] hover:bg-[#F4F4F5]'}`}
                            style={{
                                borderColor: opt.color,
                                background: severity === opt.v ? opt.color : undefined,
                            }}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
                <div className="mt-1 text-[10px] text-[#52525B]">
                    Run AI Analysis on the defect to get a suggested severity.
                </div>
            </div>
            <textarea
                data-testid="snag-notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notes (optional)"
                rows={2}
                className="w-full mt-3 border border-[#E4E4E7] focus:border-[#18181B] outline-none px-3 py-3 text-sm"
            />
            {photoProcessing ? (
                <div className="mt-3 border border-[#E4E4E7] p-6 text-center bg-[#E8F5FB]" data-testid="snag-photo-processing">
                    <CircleNotch size={24} color="#1FA5CF" weight="bold" className="mx-auto animate-spin" />
                    <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[#1787AB]">Processing photo…</div>
                    <div className="mt-1 text-[10px] text-[#52525B]">Shrinking to fit your device storage</div>
                </div>
            ) : (
                <label className="mt-3 block border border-dashed border-[#E4E4E7] p-4 text-center text-sm text-[#52525B] cursor-pointer hover:bg-[#F4F4F5]">
                    <Camera size={22} className="mx-auto" color="#1FA5CF" weight="duotone" />
                    <div className="mt-2 font-mono text-xs uppercase tracking-[0.18em]">
                        {photo ? 'Retake / change photo' : 'Take / choose photo'}
                    </div>
                    <input data-testid="snag-photo-input" type="file" accept="image/*" className="hidden" onChange={onFile} />
                </label>
            )}
            {photo && !photoProcessing && (
                <div className="mt-3 relative" data-testid="snag-photo-preview">
                    <div className="border border-[#E4E4E7] bg-[#F4F4F5] flex items-center justify-center max-h-64 overflow-hidden">
                        <AnnotationOverlay
                            photoSrc={photo}
                            annotations={photoAnnotations}
                            className="max-w-full max-h-64 object-contain block"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => setAnnotatorOpen(true)}
                        data-testid="snag-annotate-photo-btn"
                        className="absolute top-2 left-2 inline-flex items-center gap-1 bg-[#1FA5CF] text-white px-2 py-1 font-mono text-[10px] uppercase tracking-[0.18em] hover:bg-[#1787AB] active:scale-95 transition-all duration-150"
                    >
                        <PencilSimple size={10} weight="bold" /> {photoAnnotations?.length ? `Edit marks (${photoAnnotations.length})` : 'Mark up'}
                    </button>
                    <button
                        type="button"
                        onClick={() => { setPhoto(null); setPhotoAnnotations([]); }}
                        data-testid="snag-remove-photo-btn"
                        className="absolute top-2 right-2 bg-white/90 border border-[#E4E4E7] px-2 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[#E53935]"
                    >Remove</button>
                </div>
            )}
            <div className="flex gap-2 mt-4">
                <button
                    data-testid="submit-snag-btn"
                    type="submit"
                    disabled={saving || photoProcessing}
                    className={`flex-1 text-white py-3 font-mono text-xs uppercase tracking-[0.2em] active:scale-95 transition-all duration-150 inline-flex items-center justify-center gap-2 ${saving || photoProcessing ? 'bg-[#1FA5CF]/60 cursor-wait' : 'bg-[#1FA5CF] hover:bg-[#1787AB]'}`}
                >
                    {saving ? <><CircleNotch size={14} weight="bold" className="animate-spin" /> Saving…</> : photoProcessing ? 'Photo processing…' : (isEdit ? 'Save changes' : 'Log defect')}
                </button>
                <button type="button" onClick={onCancel} disabled={saving} className="px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] border border-[#18181B] hover:bg-[#F4F4F5] active:scale-95 transition-all duration-150">Cancel</button>
            </div>
            {annotatorOpen && photo && (
                <PhotoAnnotator
                    photoSrc={photo}
                    initialAnnotations={photoAnnotations}
                    onCancel={() => setAnnotatorOpen(false)}
                    onSave={(strokes) => { setPhotoAnnotations(strokes); setAnnotatorOpen(false); }}
                />
            )}
        </form>
    );
}
